import React from 'react';
import { formatCurrency, parseMonthPeriod } from '../utils/dataUtils';
import { Icon } from './Icon';

// Small trend line for the headline figure. Decorative: the same numbers are
// in the monthly chart below.
const Sparkline = ({ values }) => {
  if (!values || values.length < 2) return null;

  const width = 160;
  const height = 44;
  const pad = 5;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const points = values.map((value, index) => [
    pad + (index * (width - pad * 2)) / (values.length - 1),
    height - pad - ((value - min) / span) * (height - pad * 2)
  ]);
  const [lastX, lastY] = points[points.length - 1];

  return (
    <svg className="kpi-sparkline" viewBox={`0 0 ${width} ${height}`} width={width} height={height} aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id="kpi-spark-gradient" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2={width} y2="0">
          <stop offset="0" stopColor="#4dd9c6" />
          <stop offset="0.5" stopColor="#c6eb72" />
          <stop offset="1" stopColor="#e8d94c" />
        </linearGradient>
      </defs>
      <polyline points={points.map((point) => point.join(',')).join(' ')} fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lastX} cy={lastY} r="4" />
    </svg>
  );
};

export const SummaryCards = ({ summary, accountName, monthlyData }) => {
  const {
    totalAmount,
    totalItems,
    discountItems = 0,
    totalDiscountAmount = 0,
    trendText,
    forecastAmount,
    confidenceText
  } = summary;

  // Calculate gross amount (before discounts)
  const grossAmount = totalAmount - totalDiscountAmount;
  const hasDiscounts = totalDiscountAmount < 0;

  // Month-over-month change. Spend going up is the direction to watch, so it
  // is marked as such; the arrow and the words carry it, not colour alone.
  const renderTrend = (trend) => {
    if (!trend || trend === 'N/A') {
      return <span className="trend-indicator trend-neutral">{trend || 'N/A'}</span>;
    }

    const isUp = trend.startsWith('Up');
    return (
      <span className={isUp ? 'trend-indicator trend-up' : 'trend-indicator trend-down'}>
        <span aria-hidden="true">{isUp ? '▲' : '▼'}</span> {trend}
      </span>
    );
  };

  // Confidence dot: wider ranges are flagged more strongly
  const renderConfidenceLevel = (text) => {
    if (!text) return null;

    const percentMatch = text.match(/±(\d+)%/);
    const percent = percentMatch ? parseInt(percentMatch[1]) : 0;

    let level = 'good';
    if (percent > 15) {
      level = 'low';
    } else if (percent > 8) {
      level = 'medium';
    }

    return (
      <p className="kpi-note">
        <span className={`confidence-dot confidence-${level}`} aria-hidden="true"></span>
        {text}
      </p>
    );
  };

  const renderDiscountSavings = () => {
    if (discountItems === 0 || totalDiscountAmount === 0) {
      return <p className="kpi-note">No discounts applied</p>;
    }

    const savingsPercent = grossAmount !== 0 ? Math.abs((totalDiscountAmount / grossAmount) * 100) : 0;

    return (
      <p className="kpi-note">
        <span className="kpi-positive">{savingsPercent.toFixed(1)}% savings</span>
        {' · '}
        {discountItems} discount{discountItems !== 1 ? 's' : ''} applied
      </p>
    );
  };

  // Monthly totals in date order, for the sparkline
  const sparklineValues = monthlyData && Array.isArray(monthlyData.labels)
    ? monthlyData.labels
        .map((label, index) => ({ label, value: monthlyData.values[index] }))
        .sort((a, b) => parseMonthPeriod(a.label) - parseMonthPeriod(b.label))
        .map((entry) => entry.value)
    : [];

  return (
    <section className="kpi-grid" aria-label={`Summary for ${accountName}`}>
      <div className="kpi kpi-hero">
        <h3 className="kpi-label">Net spend</h3>
        <p className="kpi-value">{formatCurrency(totalAmount)}</p>
        <div className="kpi-hero-meta">
          {renderTrend(trendText)}
          <span className="kpi-note">Month-over-month change</span>
        </div>
        {hasDiscounts && (
          <p className="kpi-note">
            After {formatCurrency(Math.abs(totalDiscountAmount))} in discounts
          </p>
        )}
        <Sparkline values={sparklineValues} />
      </div>

      <div className="kpi-tiles">
        {/* Show gross amount if there are discounts */}
        {hasDiscounts && (
          <div className="kpi">
            <span className="kpi-icon"><Icon name="layers" /></span>
            <h3 className="kpi-label">Gross spend</h3>
            <p className="kpi-value">{formatCurrency(grossAmount)}</p>
            <p className="kpi-note">Before discounts</p>
          </div>
        )}

        <div className="kpi">
          <span className="kpi-icon"><Icon name="tag" /></span>
          <h3 className="kpi-label">Total discounts</h3>
          <p className="kpi-value">{formatCurrency(Math.abs(totalDiscountAmount))}</p>
          {renderDiscountSavings()}
        </div>

        <div className="kpi">
          <span className="kpi-icon"><Icon name="list" /></span>
          <h3 className="kpi-label">Line items</h3>
          <p className="kpi-value">{Number(totalItems || 0).toLocaleString()}</p>
          <p className="kpi-note">
            {discountItems > 0
              ? `Including ${discountItems} discount${discountItems !== 1 ? 's' : ''}`
              : 'In the selected period'}
          </p>
        </div>

        <div className="kpi">
          <span className="kpi-icon"><Icon name="trend" /></span>
          <h3 className="kpi-label">Next month forecast</h3>
          <p className="kpi-value">{formatCurrency(forecastAmount)}</p>
          {renderConfidenceLevel(confidenceText)}
        </div>
      </div>
    </section>
  );
};
